// Copyright (c) Docugami, Inc. All rights reserved.

import React, { useState, useEffect } from 'react';
import './Header.scss';
import { headerTheme, primaryTheme } from './AppStyles';
import { useLocation } from 'react-router';
import { useNavigationService, NavigationType, ACCOUNT_PAGE, BILLING_PAGE } from '../../../hooks/navigationService';
import { useDgIcons, dgIconNames } from '../../../hooks/dgIcons';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { setSettingsPanelShowing } from 'store/settings/actions';
import { DgIconButton } from '../utils/DgIconButton';
import { UserProfile } from './UserProfile';
import { NotificationsPanel } from 'components/notifications/NotificationsPanel';
import { useServiceNotificationProvider } from 'hooks/ServiceNotificationProvider';
import { InteractionStates } from 'networking/NotificationsApi';
import { Telemetry } from 'telemetry/TelemetryManager';
import { NotificationPanelOpenedSchema, SettingsPanelHiddenButtonToggledSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { AppState } from 'store';
import { selectIsLimitedOrganization, selectIsTrialSubscription } from 'store/AccountInfoHelpers';
import { TileLink } from 'pages/AccountPage';
import { Icon } from '@fluentui/react';
import { setNavigationBarVisibility } from 'store/display/actions';
import { useAppDispatch } from 'hooks/redux';
import { IconRenderer } from 'components/work_path/IconRenderer';
import { AutomationId } from 'utils/Automation';
import { internalSettingsEnabled } from 'components/settings/presentation/SettingsPanel';

const DgHeaderContainer = styled.header`
    background: ${headerTheme.semanticColors.bodyBackground};
    padding-left: 10px;
    padding-right: 5px;
`;

const DgHeaderLogoContainer = styled.div<{ onClick?: any }>`
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
    cursor: ${props => !!props.onClick ? 'pointer' : 'default'};
`;

const DgLogoDiv = styled.div`
    height: 100%;
    display: flex;
    align-items: center;
`;

const HeaderMessageText = styled.div`
    background: white;
    border-radius: 10px;
    padding: 0 10px;
    text-align: center;
    cursor: pointer;
`;

// New header consts
const HeaderButton = styled.span`
    margin-left: 15px;
    margin-right: 15px;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin-right: 15px;
`;

const HiddenButton = styled.div`
    width: 40px;
    height: 50px;
    position: fixed;
    top: 0;
    right: 0;
`;

const HiddenDiv = styled.div`
    position: absolute;
    top: -9999px;
    left: -9999px;
`;

export const DgHeader = (): JSX.Element => {
    const [showNotifications, setShowNotifications] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [docugamiClassName, setDocugamiClassName] = useState('');
    const [numClicks, setNumClicks] = useState(0);
    const [lastClicked, setLastClicked] = useState(Date.now());
    const [developerSettingsVisible, setDeveloperSettingsVisible] = useState(internalSettingsEnabled);

    const { descriptors: serviceDescriptors } = useServiceNotificationProvider();
    const navService = useNavigationService();
    const location = useLocation();
    const dgIcons = useDgIcons();
    const dispatch = useAppDispatch();

    const isLimitedOrg = useSelector((state: AppState) => !!state.organizations.organizationAccount && selectIsLimitedOrganization(state));
    const isTrialOrg = useSelector((state: AppState) => selectIsTrialSubscription(state));
    const navigationBarVisible = useSelector((state: AppState) => state.display.navigationBarVisible);

    useEffect(() => {
        const isHome = location.pathname === '/' ||
            !location.pathname ||
            location.pathname.startsWith('/home/#oid') ||
            location.pathname === '/home/';

        const docugamiClassName = isHome ? '' : 'actionable'
        setDocugamiClassName(docugamiClassName);
    }, [location.pathname]);

    useEffect(() => {
        const newUnreadCount = serviceDescriptors.filter(item => !(item.interactions & InteractionStates.Read)).length;
        setUnreadCount(newUnreadCount);
    }, [serviceDescriptors])

    function handleClick() {
        const upDate = Date.now();
        if (upDate - lastClicked > 500) {
            setNumClicks(1);
        } else {
            setNumClicks(numClicks + 1);
        }
        setLastClicked(upDate);
    }

    useEffect(() => {
        if (numClicks >= 6) {
            Telemetry.trackEvent<SettingsPanelHiddenButtonToggledSchema>({
                eventName: TelemetryEvent.SettingsPanelHiddenButtonToggled
            });
            if (!developerSettingsVisible) {
                openSettingsPanel();
            }
            setDeveloperSettingsVisible(x => !x);
            setNumClicks(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numClicks]);

    function openSettingsPanel() {
        dispatch(setSettingsPanelShowing(true));
    }

    function getHeaderButtons(): JSX.Element {
        const notificationsButton = <DgIconButton
            iconName={dgIconNames.NotificationBell}
            height={27}
            width={24}
            color={primaryTheme.palette.white}
            onClick={() => setShowNotifications(true)}
            shadow={''} />

        const settingsButton = <DgIconButton
            iconName={dgIconNames.DgSettings}
            height={27}
            width={27}
            color={primaryTheme.palette.white}
            onClick={() => { openSettingsPanel() }}
            shadow={''} />

        return (
            <ButtonContainer>
                <HeaderButton key="notifications" className="dgnotification" data-automation-id={AutomationId.NavItemNotifications}>
                    {notificationsButton}
                    {unreadCount !== 0 ? <span className="dgbadge">{unreadCount}</span> : null}</HeaderButton>
                <HeaderButton hidden={!developerSettingsVisible}>{settingsButton}</HeaderButton>
                <HeaderButton><UserProfile /></HeaderButton>
                <HiddenButton onClick={() => handleClick()} />
            </ButtonContainer>
        )
    }

    function getHeaderMessage() {
        // Don't show the expiry message if we are already on the account page
        const onAccountPage = !!location.pathname?.startsWith(`/${ACCOUNT_PAGE}`);
        const onBillingPage = !!location.pathname?.startsWith(`/${BILLING_PAGE}`);
        if (!isLimitedOrg || onAccountPage || onBillingPage) {
            return;
        }

        const message = `Your ${isTrialOrg ? 'free trial' : 'subscription'} has expired - Renew here`;
        return <HeaderMessageText onClick={() => navService.navToAccountPage(NavigationType.PushNewPage)}><TileLink style={{ fontSize: '14px' }}>{message}</TileLink></HeaderMessageText>
    }

    useEffect(() => {
        if (showNotifications) {
            Telemetry.trackEvent<NotificationPanelOpenedSchema>({
                eventName: TelemetryEvent.NotificationPanelOpened
            });
        }
    }, [showNotifications]);

    return (
        <>
            <DgHeaderContainer>
                <DgHeaderLogoContainer>
                    <Icon iconName="CollapseMenu" onClick={() => dispatch(setNavigationBarVisibility(!navigationBarVisible))} style={{ fontSize: "24px", color: "white", cursor: "pointer" }} />
                    <DgLogoDiv
                        key="docugami_brand_text"
                        onClick={() => navService.navToHomePage(NavigationType.PushNewPage)}
                        className={docugamiClassName}>
                        {dgIcons.icon(dgIconNames.DocugamiLogoNoMark, '', 16.75, 133, primaryTheme.palette.white, primaryTheme.palette.black)}
                    </DgLogoDiv>
                </DgHeaderLogoContainer>
                {getHeaderMessage()}
                {getHeaderButtons()}
            </DgHeaderContainer>
            <NotificationsPanel isShowing={showNotifications} onClose={() => setShowNotifications(false)} />
            <HiddenDiv data-automation-id={AutomationId.SubscriptionTypeIsTrial} data-automation-value={isTrialOrg} />
        </>
    );
}

interface DgUnauthenticatedHeaderProps {
    onLogoClick?: () => void;
}

export const DgUnauthenticatedHeader = (props: DgUnauthenticatedHeaderProps): JSX.Element => {
    const dgIcons = useDgIcons();
    return (
        <DgHeaderContainer>
            <DgHeaderLogoContainer onClick={props.onLogoClick}>
                <IconRenderer source={{ name: dgIconNames.DocugamiLogo, width: 0.87, height: 1, defaultColor: primaryTheme.palette.white }} width={24} height={24} containerWidth={24} />
                <DgLogoDiv key="docugami_brand_text">
                    {dgIcons.icon(dgIconNames.DocugamiLogoNoMark, '', 16.75, 133, primaryTheme.palette.white, primaryTheme.palette.black)}
                </DgLogoDiv>
            </DgHeaderLogoContainer>
        </DgHeaderContainer>
    )
}

export interface DgUnauthenticatedShellProps {
    children?: JSX.Element | JSX.Element[];
    onLogoClick?: () => void;
}

const DgUnauthenticatedShellBody = styled.div`
    flex: 1;
    width: 100%;
    overflow: auto;
    display: flex;
    height: 100%;
    flex-wrap: wrap;
`;

export const DgUnauthenticatedShell = (props: DgUnauthenticatedShellProps): JSX.Element => {
    return (
        <div className="shellFullWindow">
            <DgUnauthenticatedHeader onLogoClick={props.onLogoClick} />
            <DgUnauthenticatedShellBody>
                {props.children}
            </DgUnauthenticatedShellBody>
        </div>
    );
}
